import classNames from "classnames"
import { ChangeEventHandler, FunctionComponent, useState } from "react"

type SliderSize = "small" | "medium"

/** Thumb diameter per size. Track styling is shared; only the toggle changes. */
const THUMB_SIZE_CLASS: Record<SliderSize, string> = {
    small: "w-2.5 h-2.5", // 10px
    medium: "w-3.5 h-3.5", // 14px
}

const PALETTE = {
    dark: { track: "bg-gray-600", fill: "bg-gray-100", thumb: "bg-gray-50 border-gray-300" },
    light: { track: "bg-gray-200", fill: "bg-gray-400", thumb: "bg-white border-gray-300" },
} as const

interface SliderProps {
    value: number
    onChange: (newValue: number) => void
    className?: string
    step?: number
    min?: number
    max?: number
    size?: SliderSize
    isLightMode?: boolean
}

const Slider: FunctionComponent<SliderProps> = ({
    value,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    className,
    size = "medium",
    isLightMode = false,
}) => {
    const colors = isLightMode ? PALETTE.light : PALETTE.dark
    const [inputValue, setInputValue] = useState(value)

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const newValue = Number(e.target.value)
        setInputValue(newValue)
        if (onChange) {
            onChange(newValue)
        }
    }

    const progress = ((value - min) / (max - min)) * 100

    return (
        <div className="h-3.5 flex items-center w-full">
            <div
                className={classNames(
                    "relative w-full max-w-xs flex items-center group",
                    className
                )}
            >
                <div
                    className={classNames("w-full h-1 rounded-full overflow-hidden", colors.track)}
                >
                    <div
                        className={classNames("h-full", colors.fill)}
                        style={{ width: `${progress}%` }}
                    />
                </div>

                <div
                    className={classNames(
                        "absolute border rounded-full transform -translate-x-1/2 transition-transform duration-100 ease-in-out group-hover:scale-110",
                        THUMB_SIZE_CLASS[size],
                        colors.thumb
                    )}
                    style={{ left: `${progress}%` }}
                />

                <input
                    onKeyDown={(e) => e.preventDefault()}
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={inputValue}
                    onChange={handleChange}
                    className="absolute w-full h-full appearance-none bg-transparent opacity-0 cursor-pointer"
                />
            </div>
        </div>
    )
}

export { Slider }
